/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import ArgosMenuItem from "../../../../../../components/Form/Select/ArgosMenuItem";
import ArgosSelect from "../../../../../../components/Form/Select/ArgosSelect";
import {EAdasProgramSensorBomCheckboxTypes} from "../../../types";
import ArgosTextField from "../../../../../../components/Form/TextField/ArgosTextField";
import {useAppDispatch, useAppSelector} from "../../../../../../../redux/hooks";
import {
  IProcessedResolution,
  selectAdasProgramsState,
  selectConfigSensorWizardSelectSensorStep,
  selectConfigSensorWizardState,
  selectSensorBomData,
  setConfigSensorWizardSelectSensorStep
} from "../../../../../../../redux/ducks/AdasPrograms/slice";
import {FormControl, FormControlLabel, FormHelperText, InputLabel} from "@mui/material";
import ADAS_PROGRAMS_CONSTANTS from "../../../../constants";
import {EAdasProgramsEditSensorDialogType, ReduxSensor} from "../../types";
import ArgosCheckbox from "../../../../../../components/Form/Checkbox/ArgosCheckbox";
import {usePostRequest} from "../../../../../../../api/helper";


/** Step name to display in wizard */
export const SELECT_SENSOR_STEP_NAME = 'Select sensor'

/**
 * This wizard step allows the user to view and select a sensor to add to the
 * Bill of Materials for the ADAS Program.
 */
export const SelectSensorStep = React.memo(() => {
  const dispatch = useAppDispatch()

  const wizardState = useAppSelector(selectConfigSensorWizardState)
  const stepState = useAppSelector(selectConfigSensorWizardSelectSensorStep)
  const sensorsFromDatabase: any = useAppSelector(state => selectAdasProgramsState(state)?.sensorBom?.sensorsFromDatabase)

  const action = wizardState?.action
  const isAddGlobal = action === EAdasProgramsEditSensorDialogType.ADD_GLOBAL
  const isAddFromLocation = action === EAdasProgramsEditSensorDialogType.ADD_FROM_LOCATION
  const isEditFromTable = action === EAdasProgramsEditSensorDialogType.EDIT_FROM_TABLE
  const isEditFromLocation = action === EAdasProgramsEditSensorDialogType.EDIT_FROM_LOCATION
  const isAdd = isAddGlobal || isAddFromLocation
  const isEdit = isEditFromTable || isEditFromLocation


  const selectedSensorId = stepState?.id ?? ''
  const currentModality = stepState?.modality ?? ''

  // This request is used to get the processed resolution values for the selected sensor
  const req = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=getRes')
  const [processedResolutionValues, setProcessedResolutionValues] = useState<Record<string, IProcessedResolution> | null>(null);
  const [isLoadingProcessedResolutionValues, setIsLoadingProcessedResolutionValues] = useState(false);
  useEffect(() => {
    if (!selectedSensorId || currentModality !== EAdasProgramSensorBomCheckboxTypes.CAMERAS) {
      setProcessedResolutionValues(null);
      setIsLoadingProcessedResolutionValues(false);
      return;
    }
    setIsLoadingProcessedResolutionValues(true)
    setProcessedResolutionValues({})
    req({selectedSensorId}).then((res: any) => {
      const imagerTable = res?.imagerTable;
      if (imagerTable && imagerTable.length > 0) {
        const labelToData: Record<string, IProcessedResolution> = {};
        imagerTable.forEach((row: any) => {
          labelToData[row.label] = row;
        });
        setProcessedResolutionValues(labelToData);
      } else {
        setProcessedResolutionValues({});
      }
    }).catch(() => {
      setProcessedResolutionValues({});
    }).finally(() => {
      setIsLoadingProcessedResolutionValues(false);
    });
  }, [selectedSensorId, currentModality]);
  const processedResolution = useAppSelector(state => {
    const sensorId = stepState?.id
    const locationKey = stepState?.locationKey
    if (sensorId && locationKey) {
      return selectSensorBomData(state)?.data?.sensors?.[sensorId]?.sensorLocations?.filter((loc: any) =>
        loc.locationKey === locationKey[0]
      )[0]?.processedResolution ?? stepState?.processedResolution ?? null
    }
    return null
  })


  /**
   * Returns an array of sensor location options selected by the user for the
   * BOM within an ADAS Program.
   *
   * @returns {Array} An array of location objects.
   */
  const locationOptions = useAppSelector(state => {
    const sensorLocations = selectSensorBomData(state)?.data?.sensorLocations
    // sensor locations is an object, so we need to convert it to an array, but only add to array if not hidden. convert to array of {locationKey, sensorLocation}
    const newArr = []
    for (const key in sensorLocations) {
      if (!sensorLocations[key].hidden) {
        const loc = ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.locationOptions.find((location: any) => location.locationKey === key)
        if (loc)
          newArr.push(loc)
      }
    }
    return newArr
  })


  const handleChange = (data: any) => {
    dispatch(setConfigSensorWizardSelectSensorStep(data))
  }

  const notes = useAppSelector(state => {
    const sensorId = stepState?.id
    const locationKey = stepState?.locationKey
    if (sensorId && locationKey) {
      return selectSensorBomData(state)?.data?.sensors?.[sensorId]?.sensorLocations?.filter((loc: any) =>
        loc.locationKey === locationKey[0]
      )[0]?.notes ?? stepState?.notes
    }
    return undefined
  })

  useEffect(() => {
    handleChange({
      ...stepState,
      notes, processedResolution: processedResolution ?? null,
    })
  }, [notes, processedResolution])

  /**
   * Checkbox keys are all the checkboxes that are not sensor locations. These are used to populate the
   * sensorType dropdown
   */
  let checkboxKeys = Object.values(EAdasProgramSensorBomCheckboxTypes)
  // checkboxKeys.splice(checkboxKeys.indexOf(ECheckboxData.LIDARS), 1)
  checkboxKeys.splice(checkboxKeys.indexOf(EAdasProgramSensorBomCheckboxTypes.SENSOR_LOCATIONS), 1)
  checkboxKeys = checkboxKeys.filter((key: any) => {
    return sensorsFromDatabase?.[key]?.length > 0
  })


  /**
   * This stores all of the sensors that are at the selected location. This is used to filter out sensors that are
   * already at the selected location.
   * TODO: Figure out multiselect.
   */
  const sensorsAtSelectedLocation = useAppSelector(state => {
    const data = selectSensorBomData(state)?.data
    const lst = data?.sensorLocations?.[stepState?.locationKey?.[0] ?? '']?.sensors
    return lst
  })


  /**
   * Filter all sensors from database down to what should be available to select
   * in the "Sensor Name" field
   *
   * Always: Remove sensors that don't match the selected modality
   * If adding: Remove sensors that are already at the selected location
   * Always: Remove duplicate names from results
   */
  const filteredSensorNames = Array.from(new Set(sensorsFromDatabase?.[stepState?.modality ?? '']?.filter((_sensorData: ReduxSensor) => {
    const filteredByModality = _sensorData.sensorType === (stepState?.modality ?? '')
    const notAtSelectedLocation = isEdit ? true : !sensorsAtSelectedLocation?.includes(_sensorData.sensorId)
    return filteredByModality && notAtSelectedLocation
  }).map((sensor: ReduxSensor) => sensor.sensorName)))


  /**
   * Filter all sensors from database down to what should be available to select
   * in the "Sensor Version" field
   *
   * Always: Remove sensors that don't match the selected modality
   * Always: Remove sensors that don't match the selected name
   * If adding: Remove sensors that are already at the selected location
   */
  const filteredSensorVersions = sensorsFromDatabase?.[stepState?.modality ?? '']?.filter((_sensorData: ReduxSensor) => {
    const filteredByModality = _sensorData.sensorType === (stepState?.modality ?? '')
    const filteredByName = _sensorData.sensorName === (stepState?.name ?? '')
    const notAtSelectedLocation = isEdit ? true : !sensorsAtSelectedLocation?.includes(_sensorData.sensorId)
    return filteredByModality && filteredByName && notAtSelectedLocation
  })


  /**
   * Set the sensor ID when the version is changed
   */
  function handleSensorVersionChange(e: any) {
    const id = e.target.value
    const version = filteredSensorVersions.filter(function (sensor: any) {
      return sensor.sensorId === id
    })[0]?.version
    handleChange({...stepState, version, id})
  }

  /** Location is locked with add from location action */
  const isLocationFieldDisabled = action === EAdasProgramsEditSensorDialogType.ADD_FROM_LOCATION

  /** Disable for non-camera modalities or if camera is not sourced */
  const isProcessedResolutionFieldDisabled = stepState?.modality !== EAdasProgramSensorBomCheckboxTypes.CAMERAS || stepState?.notSourced || !selectedSensorId


  return (
    <ArgosFlexbox style={{height: '100%', width: '100%', gap: 20}}>
      <ArgosFlexbox vertical style={{height: '100%', width: "50%", gap: 15, padding: "10px"}}>
        <FormControl variant={'standard'}>
          <InputLabel id="location-select-label">
            Sensor Location
          </InputLabel>
          <ArgosSelect
            id="location-select"
            labelId="location-select-label"
            inputProps={{"data-testid": "location-select"}}
            required={!isLocationFieldDisabled}
            disabled={isLocationFieldDisabled}
            value={stepState?.locationKey ?? []}
            onChange={(e) => {
              const {value} = e.target;
              const val = typeof value === 'string' ? value.split(',') : value
              handleChange({...stepState, locationKey: val as string[]})
            }}
            renderValue={(selected: any) => {
              const locationsForThisSensor = selected.map((locationKey: any) => {
                const locationObj = locationOptions.find((location: any) => location.locationKey === locationKey)
                const fullLocationName = locationObj?.sensorLocation
                return `${fullLocationName} (${locationKey})`
              })
              return locationsForThisSensor.join(', ')
            }}
          >
            {locationOptions.map((location: any, i: number) => {
              const fullLocationName = location.sensorLocation
              const _locationKey = stepState?.locationKey ?? []
              const displayValue = `${fullLocationName} (${location.locationKey})`
              return (
                <ArgosMenuItem key={location.locationKey} value={location.locationKey}>
                  <ArgosCheckbox checked={_locationKey.includes(location.locationKey)}/>
                  <span>{displayValue}</span>
                </ArgosMenuItem>
              )
            })}
          </ArgosSelect>
        </FormControl>

        <FormControl variant={'standard'}>
          <InputLabel id="modality-select-label">
            Sensor Modality
          </InputLabel>
          <ArgosSelect
            id="modality-select"
            labelId="modality-select-label"
            inputProps={{"data-testid": "modality-select"}}
            required
            disabled={stepState?.locationKey?.length === 0 || stepState?.locationKey === undefined}
            value={stepState?.modality ?? ''}
            onChange={(e) => {
              let _processedResolution = {}
              if (e.target.value !== EAdasProgramSensorBomCheckboxTypes.CAMERAS) {
                /** Clear processed resolution field when changing to any non-camera modality */
                _processedResolution = {processedResolution: null}
              }
              /** Clear name, version, and ID when changing modality */
              handleChange({
                ...stepState, ..._processedResolution,
                modality: e.target.value as string, name: "", version: "", id: "",
              })
            }}
          >
            {checkboxKeys.map((_sensorType, i) => {
              return (
                <ArgosMenuItem key={i} value={_sensorType}>
                  {_sensorType}
                </ArgosMenuItem>
              )
            })}
          </ArgosSelect>
        </FormControl>

        <FormControl variant={'standard'}>
          <InputLabel id="name-select-label">
            Sensor Name
          </InputLabel>
          <ArgosSelect
            id="name-select"
            labelId="name-select-label"
            inputProps={{"data-testid": "name-select"}}
            required
            disabled={stepState?.modality === '' || stepState?.modality === undefined || stepState?.notSourced}
            error={false}
            value={stepState?.name}
            onChange={(e) => {
              /** Clear version and ID when changing name */
              handleChange({
                ...stepState,
                name: e.target.value as string, id: "", version: ""
              })
            }}
          >
            {filteredSensorNames?.length > 0
              ? filteredSensorNames.map((name: any, i: number) => {
                return (
                  <ArgosMenuItem key={i} value={name}>
                    {name}
                  </ArgosMenuItem>
                )
              })
              : <ArgosMenuItem value={stepState?.name as string}>
                {stepState?.name}
              </ArgosMenuItem>
            }
          </ArgosSelect>
        </FormControl>

        <FormControl variant={'standard'}>
          <InputLabel id="version-select-label">
            Sensor Version
          </InputLabel>
          <ArgosSelect
            id="version-select"
            labelId="version-select-label"
            inputProps={{"data-testid": "version-select"}}
            required
            disabled={stepState?.name === '' || stepState?.name === undefined || stepState?.notSourced}
            error={false}
            value={stepState?.id}
            onChange={(e) => {
              handleSensorVersionChange(e)
            }}
          >
            {filteredSensorVersions?.length > 0
              ? filteredSensorVersions.map((_sensorData: ReduxSensor, i: number) => {
                return (
                  <ArgosMenuItem key={i} value={_sensorData.sensorId}>
                    v{_sensorData.version}
                  </ArgosMenuItem>
                )
              })
              : <ArgosMenuItem value={stepState?.id as string}>
                {stepState?.version}
              </ArgosMenuItem>
            }
          </ArgosSelect>
        </FormControl>

        {
          // If loading processed resolution values, show loading message
          isLoadingProcessedResolutionValues ? (
              <span>Loading processed resolution values...</span>
            ) :
            // else if not disable but no processed resolutions, show error message
            !isProcessedResolutionFieldDisabled && (!processedResolutionValues || Object.keys(processedResolutionValues).length === 0) ? (
                <div style={{color: 'red', marginTop: 8}}>
                  No processed resolutions found for this sensor.
                </div>
              ) :
              // else show the processed resolution select
              (
                <FormControl variant={'standard'}>
                  <InputLabel id="processed-resolution-select-label">
                    Processed Resolution
                  </InputLabel>
                  <ArgosSelect
                    id="processed-resolution-select"
                    labelId="processed-resolution-select-label"
                    inputProps={{"data-testid": "processed-resolution-select"}}
                    required
                    disabled={isProcessedResolutionFieldDisabled}
                    value={stepState?.processedResolution?.label ?? ""}
                    onChange={(e) => {
              const selectedLabel = e.target.value as string;
              const selectedObj = processedResolutionValues?.[selectedLabel];
              handleChange({...stepState, processedResolution: selectedObj});
            }}
                  >
                    {processedResolutionValues != null &&
                      Object.keys(processedResolutionValues).map((label) => (
                        <ArgosMenuItem key={label} value={label}>
                          {label} - {processedResolutionValues[label].mp} MP
                        </ArgosMenuItem>
                      ))}
                  </ArgosSelect>
                </FormControl>
              )}

        <FormControl variant="standard">
          <FormControlLabel
            id="sensor-not-sourced-label"
            data-testid="sensor-not-sourced-label"
            control={
              <ArgosCheckbox
                id="sensor-not-sourced-checkbox"
                data-testid="sensor-not-sourced-checkbox"
                checked={stepState?.notSourced ?? false}
                onChange={(e) => {
                  const notSourcedId: string | undefined = `${stepState?.["modality"]}-not-sourced`
                  handleChange({...stepState, processedResolution: null, name: "", version: "", id: e.target.checked ? notSourcedId : "", notSourced: e.target.checked})
                }}
              />
            }
            label="Sensor not yet sourced"
          />
          <FormHelperText>Add as a placeholder until the final sensor is identified</FormHelperText>
        </FormControl>
      </ArgosFlexbox>

      <ArgosFlexbox vertical style={{height: '100%', width: "50%", padding: "10px"}}>
        <ArgosTextField
          id="notes-textfield"
          inputProps={{"data-testid": "notes-textfield"}}
          label={"Notes"}
          multiline
          rows={17}
          value={stepState?.notes}
          onChange={(e) => {
            handleChange({...stepState, notes: e.target.value})
          }}
        />
      </ArgosFlexbox>
    </ArgosFlexbox>
  )
})